/**
 * Validates a bot version name
 * @param {string} versionName - The version name to validate
 * @param {Array<string>} existingVersions - Array of existing version names in the group
 * @param {string} [currentVersion] - The current version name (for edit scenarios, optional)
 * @returns {Object} Validation result
 * @returns {boolean} .isValid - Whether the version name is valid
 * @returns {string} .errorType - Type of error: 'empty' | 'reserved' | 'duplicate' | null
 * @returns {string} .errorMessageId - i18n message ID for the error
 */
export const validateBotVersionName = (versionName, existingVersions = [], currentVersion = null) => {
    const trimmedName = versionName?.trim() || '';

    // Empty string
    if (!trimmedName) {
        return {
            isValid: false,
            errorType: 'empty',
            errorMessageId: 'botVersion.cannotBeEmpty'
        };
    }

    // Reserved word "DEFAULT"
    if (trimmedName.toLowerCase() === 'default') {
        return {
            isValid: false,
            errorType: 'reserved',
            errorMessageId: 'botVersionCantBeDefault'
        };
    }

    // Duplicate, excluding current version
    if (existingVersions && existingVersions.length > 0) {
        const isDuplicate = existingVersions.some(v =>
            v.toLowerCase() === trimmedName.toLowerCase() &&
            (!currentVersion || v.toLowerCase() !== currentVersion.toLowerCase())
        );

        if (isDuplicate) {
            return {
                isValid: false,
                errorType: 'duplicate',
                errorMessageId: 'botVersion.duplicateName'
            };
        }
    }

    return {
        isValid: true,
        errorType: null,
        errorMessageId: null
    };
};

/**
 * Gets default error message for a validation error type
 * @param {string} errorType - The error type ('empty' | 'reserved' | 'duplicate')
 * @returns {string} Default error message
 */
export const getDefaultErrorMessage = (errorType) => {
    switch (errorType) {
        case 'empty':
            return 'Version name cannot be empty';
        case 'reserved':
            return 'Default is already taken. Please use a different name.';
        case 'duplicate':
            return 'This version name already exists. Please use a different name.';
        default:
            return 'Invalid version name';
    }
};
